import React from "react";
import { FaBell, FaEdit, FaEnvelope, FaEye, FaTrash } from "react-icons/fa";

import {
  formatEuro,
  getPaymentStatusMeta,
  getReminderCount,
} from "../utils/paymentsDashboardUtils";

function formatDateShort(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function PaymentUserCell({ user, adherents }) {
  const list = Array.isArray(adherents) ? adherents : [];

  return (
    <div className="min-w-0">
      <p className="truncate font-bold text-slate-900 dark:text-white">
        {user?.prenom || ""} {user?.nom || ""}
      </p>

      {user?.email ? (
        <p
          className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400"
          title={user.email}
        >
          {user.email}
        </p>
      ) : null}

      {list.length ? (
        <p
          className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400"
          title={list
            .map((a) => `${a.prenom || ""} ${a.nom || ""}`.trim())
            .join(", ")}
        >
          {list
            .slice(0, 2)
            .map((a) => `${a.prenom || ""} ${a.nom || ""}`.trim())
            .join(", ")}
          {list.length > 2 ? ` +${list.length - 2}` : ""}
        </p>
      ) : null}
    </div>
  );
}

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaEye />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun paiement trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Modifiez les filtres ou ajoutez un nouveau règlement.
        </p>
      </div>
    </section>
  );
}

function ActionButton({
  title,
  onClick,
  disabled,
  children,
  tone = "default",
  className = "",
}) {
  const tones = {
    default:
      "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    info: "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
    warn: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-8 items-center justify-center rounded-xl border text-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.default
      } ${className || "w-8"}`}
    >
      {children}
    </button>
  );
}

function ReminderCountButton({ payment, onOpenReminderHistory }) {
  const count = getReminderCount(payment);

  return (
    <ActionButton
      title="Voir l'historique des rappels"
      onClick={() => onOpenReminderHistory(payment)}
      tone="warn"
      className="min-w-8 gap-1 px-2"
    >
      <FaBell className="text-[12px]" />
      <span className="text-xs font-bold tabular-nums">{count}</span>
    </ActionButton>
  );
}

export default function PaymentsTable({
  payments,
  loading,
  adherentsByUser,
  togglingAuthorizationId,
  deletingId,
  remindingId,
  onToggleAuthorization,
  onSendReminder,
  onOpenReminderHistory,
  onEdit,
  onDelete,
  onOpenDetails,
}) {
  if (loading) return <SkeletonRows />;
  if (!payments.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Règlements
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Tableau des paiements
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Le bouton cloche indique le nombre de rappels envoyés et ouvre leur
            historique.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1180px] table-fixed">
            <colgroup>
              <col className="w-[120px]" />
              <col className="w-[130px]" />
              <col className="w-[96px]" />
              <col className="w-[210px]" />
              <col className="w-[310px]" />
              <col className="w-[78px]" />
              <col className="w-[172px]" />
            </colgroup>

            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="sticky left-0 z-30 border-r border-slate-200 bg-slate-50 px-3 py-3 text-left font-bold dark:border-slate-700 dark:bg-slate-950">
                  Statut
                </th>

                <th className="sticky left-[120px] z-30 border-r border-slate-200 bg-slate-50 px-3 py-3 text-center font-bold dark:border-slate-700 dark:bg-slate-950">
                  Montant
                </th>

                <th className="px-3 py-3 text-left font-bold">Date</th>

                <th className="px-3 py-3 text-left font-bold">
                  Utilisateur
                </th>

                <th className="px-3 py-3 text-left font-bold">
                  Article / détail
                </th>

                <th className="px-3 py-3 text-center font-bold">Auto.</th>

                <th className="px-3 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/60 text-sm dark:divide-slate-800">
              {payments.map((payment) => {
                const meta = getPaymentStatusMeta(payment.statut_reglement);
                const adherents = adherentsByUser?.[payment.User?.id] || [];
                const articleName =
                  payment.Article?.nom || "Règlement Inscription";
                const articleSize = payment.Article?.taille || "";

                const isClosableStatus = [
                  "Réglé",
                  "Annulé",
                  "Remboursé",
                ].includes(payment.statut_reglement);

                return (
                  <tr key={payment.id} className={`transition ${meta.row}`}>
                    <td
                      className={`sticky left-0 z-20 border-r border-slate-200 px-3 py-4 align-top dark:border-slate-700 ${meta.sticky}`}
                    >
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${meta.badge}`}
                      >
                        {payment.statut_reglement || "—"}
                      </span>
                    </td>

                    <td
                      className={`sticky left-[120px] z-20 border-r border-slate-200 px-3 py-4 text-center align-top dark:border-slate-700 ${meta.sticky}`}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <p className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                          {formatEuro(payment.prix_total)}
                        </p>

                        <p className="mt-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          {payment.type_reglement || "—"}
                        </p>
                      </div>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="font-medium text-slate-700 dark:text-slate-200">
                        {formatDateShort(payment.date_achat)}
                      </div>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <PaymentUserCell
                        user={payment.User}
                        adherents={adherents}
                      />
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="min-w-0">
                        <p
                          className="line-clamp-2 font-bold text-slate-900 dark:text-white"
                          title={articleName}
                        >
                          {articleName}
                        </p>

                        <div className="mt-1 space-y-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
                          <p>Qté : {payment.quantite ?? 1}</p>

                          {articleSize ? <p>Taille : {articleSize}</p> : null}

                          {payment.message_achat ? (
                            <button
                              type="button"
                              onClick={() => onOpenDetails(payment)}
                              className="inline-flex items-center gap-1 font-bold text-indigo-700 underline underline-offset-2 hover:opacity-80 dark:text-indigo-300"
                            >
                              <FaEye className="text-[11px]" />
                              Voir le message
                            </button>
                          ) : (
                            <p className="text-slate-400 dark:text-slate-500">
                              Aucun message
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-4 text-center align-top">
                      <button
                        type="button"
                        onClick={() => onToggleAuthorization(payment)}
                        disabled={togglingAuthorizationId === payment.id}
                        title="Afficher ou masquer le paiement côté utilisateur"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-base transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                      >
                        {payment.autorisation_achat ? "✅" : "❌"}
                      </button>
                    </td>

                    <td className="px-3 py-4 align-top">
                      <div className="flex items-center justify-end gap-1.5">
                        <ActionButton
                          title="Voir le détail"
                          onClick={() => onOpenDetails(payment)}
                          tone="info"
                        >
                          <FaEye />
                        </ActionButton>

                        {!isClosableStatus ? (
                          <ActionButton
                            title="Envoyer un rappel"
                            onClick={() => onSendReminder(payment)}
                            disabled={remindingId === payment.id}
                            tone="warn"
                          >
                            <FaEnvelope />
                          </ActionButton>
                        ) : null}

                        <ReminderCountButton
                          payment={payment}
                          onOpenReminderHistory={onOpenReminderHistory}
                        />

                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(payment)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(payment)}
                          disabled={deletingId === payment.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
          La colonne statut et la colonne montant restent visibles à gauche lors
          du défilement horizontal.
        </p>
      </div>
    </section>
  );
}